import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type {
  Exercise,
  Plan,
  PlanSeed,
  PlanStarter,
  ProgramModel,
  Session,
  UnitsPreference,
} from "@heraion/shared";
import { RHYTHM_KIND_KEY, deleteSessionsInOrder, useWorkoutsDiary, workoutReader, writeError, type UiTheme } from "@heraion/ui";

import { apiFetch } from "./api";
import { useAuth } from "./auth/AuthContext";
import { useAccountKey } from "./auth/useAccountKey";
import { confirmViaWindow, notifyViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { fetchSessionDetail } from "./useSessionLoader";
import { useWorkoutStarter } from "./useWorkoutStarter";

const readWorkout = workoutReader(fetchSessionDetail);

// #368: the web's mount of the diary (useWorkoutsDiary). The wide console
// gets the Select button beside List | Calendar; a hold works on both
// widths. The web deletes too now, from the selection bar. Plan, a plan of
// the calendar and «Create program» open the Home surface's editors through
// the shell (AppShell's requestComposer); a started plan opens the logger
// this screen holds.
export function useWebDiary({
  theme,
  wide,
  items,
  allLoaded,
  unit,
  catalog,
  bodyweightKg,
  resolveName,
  onRefresh,
  onDeleted,
  onStarter,
  onPlanSession,
  onOpenPlan,
  onOpenSession,
  onPlanFromModel,
}: {
  theme: UiTheme;
  wide: boolean;
  items: Session[] | null;
  /** No older page of sessions is left. */
  allLoaded: boolean;
  /** Null until the profile is read: the diary's plan and model wait for it. */
  unit: UnitsPreference | null;
  catalog: Exercise[] | null;
  bodyweightKg: number | null;
  resolveName: (slug: string, snapshot?: string) => string;
  onRefresh: () => void;
  /** The ids that are gone: the detail pane lets go of one of them. */
  onDeleted: (ids: ReadonlySet<string>) => void;
  onStarter: (starter: PlanStarter) => void;
  onPlanSession?: (seed: PlanSeed) => void;
  onOpenPlan?: (plan: Plan) => void;
  /** #374: a workout of the calendar's day, on its detail. */
  onOpenSession: (session: Session) => void;
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
}) {
  const { t } = useTranslation();
  const { starting, startPlan } = useWorkoutStarter(catalog, bodyweightKg, onStarter);
  // #499: the caller's own sub: a plan they wrote for other athletes is not
  // theirs to miss on the calendar.
  const { state } = useAuth();
  const sub = state.status === "signed_in" ? state.user.sub : null;
  const kindKey = useAccountKey(RHYTHM_KIND_KEY);
  // The diary says when some were not deleted; the reason goes to the log.
  const deleteSessions = useCallback(
    async (ids: readonly string[]) => {
      const { gone, refused } = await deleteSessionsInOrder(durableWrite, ids);
      if (refused) console.warn("[workouts] delete_failed", writeError(refused)?.message);
      if (gone.length > 0) {
        onDeleted(new Set(gone));
        onRefresh();
      }
      return gone;
    },
    [onDeleted, onRefresh],
  );
  return useWorkoutsDiary({
    theme,
    items,
    allLoaded,
    sub,
    kindKey,
    showSelectButton: wide,
    dockedBar: false,
    apiFetch,
    durableWrite,
    confirm: confirmViaWindow,
    readWorkout,
    deleteSessions,
    onPlanSeed: (seed) => onPlanSession?.(seed),
    onPlanFromModel: (model) => onPlanFromModel?.(model),
    onOpenPlan: (plan) => onOpenPlan?.(plan),
    onOpenSession,
    onStartPlan: (plan) => void startPlan(plan),
    startingPlanId: starting,
    unit,
    catalog,
    resolveName,
    onError: (message) => notifyViaWindow(t("errors.generic"), message),
  });
}
